// Utility functions for handling extended brand voice properties
// These properties are stored in the hidden_link_c field. We keep legacy JSON support
// but default to a compact key/value encoding to stay under the 255-char limit.

const DEFAULT_EXTENDED = {
  adStyles: [],
  visualStyles: [],
  awarenessLevel: "",
  problemIntensity: "",
  priorityObjective: "",
  hookStyle: "",
  ctaApproach: "",
  toneStyle: "",
  copyLength: "",
  formattingStyle: "",
  aiCreativity: 60
};

const adStyleCodes = {
  "Product Demo": "pd",
  "Before & After": "ba",
  "Problem - Solution": "ps",
  "Case Study / Story": "cs",
  "Social Proof / Testimonial": "sp",
  "Results / Data-Proof": "rd",
  "Comparative (Us vs. Them)": "cmp",
  "Lifestyle Fit": "life",
  "Founder / Authority Message": "auth",
  "How-To / Tutorial": "how",
  "Press / \"As Featured In\"": "pr",
  "Offer / Promo / Limited-Time": "off"
};

const visualStyleCodes = {
  "Abstract / Conceptual / Iconic": "abs",
  "Aspirational / Emotional Appeal": "asp",
  "Founder / Brand Story / Personal": "fst",
  "Humorous / Playful": "fun",
  "Direct Message / Clean / Minimal": "dm",
  "Lifestyle / In-Use / IRL": "irl",
  "Testimonial / Social Proof": "tsp",
  "User-Generated Content (UGC)": "ugc",
  "Data-Driven / Infographic": "data",
  "Minimalist / Text-focused": "min",
  "Multi-Panel / Grid Layout": "grid",
  "Promotional / Offer-focused": "promo",
  "Feature Highlight / Product Beauty": "feat",
  "How-To / Step-by-Step": "how",
  "Illustrated / Visual Metaphor": "illus",
  "Product-Centric / Hero Shot": "hero",
  "Unboxing / First Impressions": "unbox",
  "Before & After (Visual)": "ba_v",
  "Comparison / Split-Screen": "comp",
  "Problem - Solution (Visual)": "ps_v"
};

const awarenessCodes = {
  "Unaware": "ua",
  "Problem Aware": "pa",
  "Solution Aware": "sa",
  "Product Aware": "pra",
  "Most Aware": "ma"
};

const problemIntensityCodes = {
  "Calm: light concern": "calm",
  "Even: moderate concern": "even",
  "Urgent: high concern": "urg",
  "Crisis: intense concern": "cris"
};

const priorityObjectiveCodes = {
  "Lead Gen": "lg",
  "Sales": "sale",
  "Traffic": "traf",
  "Product Announce": "prod",
  "Brand Lift": "lift",
  "Loyalty/Retention": "loyal",
  "Upsell/Cross-sell": "upx"
};

const hookStyleCodes = {
  "Aspirational": "asp",
  "Empathy": "emp",
  "Pain": "pain",
  "Solution": "sol",
  "Expertise": "exp",
  "Product": "prod",
  "Value": "val",
  "Story": "story",
  "Urgency": "urg"
};

const ctaApproachCodes = {
  "Direct CTA": "dir",
  "Soft CTA": "soft",
  "Consultative": "cons",
  "Guarantee": "gua",
  "Value Swap": "swap",
  "Deadline": "dead",
  "Donation": "don",
  "Education": "edu",
  "Explore": "expl",
  "Validation": "val",
  "Invitation": "inv"
};

const toneStyleCodes = {
  "Informative": "info",
  "Conversational": "conv",
  "Empathetic": "emp",
  "Motivating": "mot",
  "Encouraging": "enc",
  "Confident": "conf",
  "Playful": "play",
  "Punchy": "punc",
  "Inspirational": "insp"
};

const copyLengthCodes = {
  "Short form (1-2 sentences)": "short",
  "Medium form (3-5 sentences)": "med",
  "Long form (6-9 sentences)": "long",
  "Extended story (10+ sentences)": "ext"
};

const formattingStyleCodes = {
  "Single paragraph": "para",
  "Bullet points": "bullet",
  "Line breaks": "line",
  "Micro-stories": "micro"
};

const invert = (obj) => Object.fromEntries(Object.entries(obj).map(([k, v]) => [v, k]));

const decodeList = (value = "", map, splitter = ",") =>
  value
    .split(splitter)
    .map((code) => code.trim())
    .filter(Boolean)
    .map((code) => map[code] || "")
    .filter(Boolean);

const encodeList = (list = [], map) => {
  if (!Array.isArray(list)) return [];
  return Array.from(
    new Set(
      list
        .map((item) => (typeof item === "string" ? item.trim() : ""))
        .filter(Boolean)
        .map((item) => map[item] || "")
        .filter(Boolean)
    )
  );
};

const decodeChoice = (value = "", map) => map[value] || "";
const encodeChoice = (value = "", map) => map[value] || "";

const MAX_LENGTH = 255;

export const parseExtendedBrandVoice = (hiddenLinkData) => {
  if (!hiddenLinkData || hiddenLinkData.trim() === "") {
    return { ...DEFAULT_EXTENDED };
  }

  // Try JSON first (legacy)
  try {
    const parsed = JSON.parse(hiddenLinkData);
    return {
      ...DEFAULT_EXTENDED,
      ...parsed
    };
  } catch {
    // fall through to compact format
  }

  // Compact format: key=value;key=value with comma-separated codes
  const parts = hiddenLinkData.split(";").map((p) => p.trim()).filter(Boolean);
  const kv = {};
  parts.forEach((part) => {
    const [k, v = ""] = part.split(":");
    if (k) kv[k.trim()] = v.trim();
  });

  const invAd = invert(adStyleCodes);
  const invVisual = invert(visualStyleCodes);

  return {
    adStyles: decodeList(kv.as || "", invAd),
    visualStyles: decodeList(kv.vs || "", invVisual),
    awarenessLevel: decodeChoice(kv.aw || "", invert(awarenessCodes)),
    problemIntensity: decodeChoice(kv.pi || "", invert(problemIntensityCodes)),
    priorityObjective: decodeChoice(kv.po || "", invert(priorityObjectiveCodes)),
    hookStyle: decodeChoice(kv.hk || "", invert(hookStyleCodes)),
    ctaApproach: decodeChoice(kv.cta || "", invert(ctaApproachCodes)),
    toneStyle: decodeChoice(kv.tone || "", invert(toneStyleCodes)),
    copyLength: decodeChoice(kv.len || "", invert(copyLengthCodes)),
    formattingStyle: decodeChoice(kv.fmt || "", invert(formattingStyleCodes)),
    aiCreativity: Number.isFinite(Number(kv.ai)) ? Number(kv.ai) : 60
  };
};

export const serializeExtendedBrandVoice = (brandVoice) => {
  if (!brandVoice) return "";

  const encoded = {
    as: encodeList(brandVoice.adStyles, adStyleCodes).join(","),
    vs: encodeList(brandVoice.visualStyles, visualStyleCodes).join(","),
    aw: encodeChoice(brandVoice.awarenessLevel, awarenessCodes),
    pi: encodeChoice(brandVoice.problemIntensity, problemIntensityCodes),
    po: encodeChoice(brandVoice.priorityObjective, priorityObjectiveCodes),
    hk: encodeChoice(brandVoice.hookStyle, hookStyleCodes),
    cta: encodeChoice(brandVoice.ctaApproach, ctaApproachCodes),
    tone: encodeChoice(brandVoice.toneStyle, toneStyleCodes),
    len: encodeChoice(brandVoice.copyLength, copyLengthCodes),
    fmt: encodeChoice(brandVoice.formattingStyle, formattingStyleCodes),
    ai: Math.min(100, Math.max(0, Math.round(brandVoice.aiCreativity ?? 60)))
  };

  const compact = Object.entries(encoded)
    .filter(([, v]) => v !== "" && v !== undefined && v !== null)
    .map(([k, v]) => `${k}:${v}`)
    .join(";");

  if (compact.length <= MAX_LENGTH) return compact;

  // Fallback to truncated compact string if somehow still too long
  return compact.slice(0, MAX_LENGTH);
};
